import { Big } from 'big.js'

import type { Currency, Language } from '../types/localeTypes'
import type { Optional } from '../types/types'
import { isEmpty } from './typeUtils'

const DEFAULT_ROUNDING_PRECISION = 2

type FormatAmountProps = {
  value: Optional<number | string>
  currency?: Currency
  language?: Language
  isTwoDigitFraction?: boolean
}

export const addMaybe = (a: Optional<number>, b: Optional<number>) =>
  typeof a === 'number' || typeof b === 'number' ? (a ?? 0) + (b ?? 0) : null

export const invertMaybe = (value: Optional<number>) => {
  if (value === 0) {
    return 0
  }
  return typeof value === 'number' ? -value : null
}

export const formatAmount = ({
  value,
  currency,
  isTwoDigitFraction = false,
  language = 'en-GB',
}: FormatAmountProps) => {
  if (isEmpty(value)) {
    return '-'
  }

  if (isNaN(Number(value))) {
    throw new Error(`Unable to parse ${value} as a number`)
  }

  const fractionDigits = isTwoDigitFraction ? 2 : 0

  return Intl.NumberFormat(language, {
    ...(currency && { style: 'currency', currency }),
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Number(value))
}

export const formatPercent = (amount: Optional<number>) =>
  formatPercentWithDecimals(amount, 0)

export const formatPercentWithDecimals = (
  amount: Optional<number>,
  decimals = DEFAULT_ROUNDING_PRECISION,
) => (isEmpty(amount) ? '-' : `${new Big(amount).mul(100).toFixed(decimals)}%`)

export const frameCardinalityBounds = (frameCardinality: Optional<string>) => {
  if (isEmpty(frameCardinality)) {
    return [0, 0]
  } else if (frameCardinality === 'zeroToOne') {
    return [0, 1]
  } else if (frameCardinality === 'one') {
    return [1, 1]
  } else if (frameCardinality === 'zeroToMany') {
    return [0, Infinity]
  } else if (frameCardinality === 'oneToMany') {
    return [1, Infinity]
  } else {
    // Unknown FrameCardinality variant.
    return [0, 0]
  }
}

export const formatFileSize = (bytes: number) => {
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']

  if (bytes == 0) {
    return 'n/a'
  }

  const i = Math.floor(Math.log(bytes) / Math.log(1024))

  if (i == 0) {
    return `${bytes} ${sizes[i]}`
  }

  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
}

/**
 * Round a floating point number to have a certain precision in the fractional part.
 *
 * Note that due to the way floating point numbers work,
 * the string representation of the resulting number
 * may still render digits beyond what was specified.
 */
export const roundToPrecision = (
  number: number,
  fractionDigits = DEFAULT_ROUNDING_PRECISION,
) => new Big(number).round(fractionDigits).toNumber()

/**
 * Sum multiple values, rounding to two decimal digits afterwards.
 *
 * This function should be used when adding together monetary amounts,
 * because floats cannot accurately represent all decimal numbers, and
 * a naive addition can yield surprising results (0.1+0.1+0.1 === 0.30000000000000004).
 */
export function sumWithRounding(values: number[]): number
export function sumWithRounding(
  values: Optional<readonly Optional<number>[]>,
): Optional<number>

export function sumWithRounding(
  values: Optional<readonly Optional<number>[]>,
): Optional<number> {
  return isEmpty(values) || (values.length && values.every(isEmpty))
    ? undefined
    : values
        .reduce<Big>((acc, value) => acc.add(new Big(value ?? 0)), new Big(0))
        .round(DEFAULT_ROUNDING_PRECISION)
        .toNumber()
}
